/**
 * PowerKid SPA Entry Point
 *
 * This is the standalone SPA entry point that replaces the native web app.
 * It provides client-side routing for the entire web application.
 */

import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Import apps
import { LandingPage } from './apps/landing/index';
import LoginApp from './apps/login/LoginApp';
import AuthCallback from './apps/auth/AuthCallback';
import { App as UnifiedDashboard, DASHBOARD_MODES } from './apps/unified-dashboard/index';
import PricingPage from './apps/pricing/PricingPage';
import FeaturesPage from './apps/features/FeaturesPage';
import ProgramsPage from './apps/programs/ProgramsPage';
import { AuthProvider, useAuth } from './shared/auth/index';
import { initI18n } from './shared/i18n/index';

// Create query client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: 2,
      staleTime: 30000 // 30 seconds
    }
  }
});

// Get Rails data from window (injected by SpaController)
const getRailsData = () => {
  if (typeof window === 'undefined') {
    return {
      csrfToken: '',
      locale: 'vi',
      flash: {},
      assetManifest: {},
      railsEnv: 'production',
      onboarding: null,
      enabledProviders: {},
      kidDomainMode: false
    };
  }
  return window.RAILS_DATA || {
    csrfToken: '',
    locale: 'vi',
    flash: {},
    assetManifest: {},
    railsEnv: 'production',
    onboarding: null,
    enabledProviders: {},
    kidDomainMode: false
  };
};

// Get current path from window.location
const getCurrentPath = () => {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname || '/';
};

// Navigate to a path (full page load to ensure server-side session is used)
const navigateTo = (path) => {
  console.log('[SPA] navigateTo() called with path:', path);
  // Use full page navigation to ensure HttpOnly cookies are sent and auth state is fresh
  window.location.href = path;
};

// Get flash message from URL query params
const getFlashFromQuery = () => {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const notice = params.get('notice') || undefined;
  const alert = params.get('alert') || undefined;

  // Clear the query params from URL after reading (without triggering navigation)
  if (notice || alert) {
    const url = new URL(window.location.href);
    url.searchParams.delete('notice');
    url.searchParams.delete('alert');
    window.history.replaceState({}, '', url.toString());
  }

  return { notice, alert };
};

// Simple URL-based router
const getRouteFromPath = (path) => {
  if (path === '/' || path === '' || path === '/welcome') {
    return 'landing';
  }
  if (path === '/auth/callback') {
    return 'auth_callback';
  }
  if (path.startsWith('/login')) {
    return 'login';
  }
  if (path === '/pricing') {
    return 'pricing';
  }
  if (path === '/features') {
    return 'features';
  }
  if (path === '/programs') {
    return 'programs';
  }
  if (path.startsWith('/children')) {
    return 'children';
  }
  if (path.startsWith('/guardian')) {
    return 'guardian';
  }
  return 'landing';
};

// Loading Screen Component
const LoadingScreen = () => (
  <div className="d-flex min-vh-100 bg-light align-items-center justify-content-center">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

// Fetch auth status from server
const fetchAuthStatus = async () => {
  try {
    const response = await fetch('/api/auth/me', {
      credentials: 'include',
      headers: {
        'Accept': 'application/json'
      }
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
};

// Fetch onboarding status from API
const fetchOnboardingStatus = async () => {
  try {
    const response = await fetch('/api/onboarding/status', {
      credentials: 'include',
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
};

// Cache for initial flash params
let initialFlashCache = null;
const getInitialFlash = () => {
  if (initialFlashCache === null) {
    if (typeof window !== 'undefined') {
      const railsData = getRailsData();
      const railsFlash = railsData.flash || {};
      const queryFlash = getFlashFromQuery();
      initialFlashCache = {
        notice: railsFlash.notice || queryFlash.notice,
        alert: railsFlash.alert || queryFlash.alert,
      };
    } else {
      initialFlashCache = { notice: undefined, alert: undefined };
    }
  }
  return initialFlashCache;
};

// Notice shown when an already-authenticated user lands on /login.
// Avoids the confusing UX of presenting a fresh login form to a signed-in user.
const AlreadyLoggedInNotice = ({ userType, dashboardPath }) => {
  const isKid = userType === 'kid';
  const testIdRoot = isKid ? 'already-logged-in-kid' : 'already-logged-in-guardian';
  const linkTestId = isKid ? 'kid-dashboard-link' : 'guardian-dashboard-link';
  const heading = isKid
    ? 'Bạn đã đăng nhập với tư cách trẻ'
    : 'Bạn đã đăng nhập với tư cách phụ huynh';
  const linkLabel = isKid ? 'Tới Bảng Điều Khiển Trẻ' : 'Tới Bảng Điều Khiển Phụ Huynh';

  const handleSwitchAccount = async (e) => {
    e.preventDefault();
    try {
      await fetch('/logout', {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        }
      });
    } catch (err) {
      console.error('[SPA] Logout error during account switch:', err);
    }
    window.location.href = '/login';
  };

  return (
    <div
      className="container d-flex justify-content-center align-items-center py-5"
      style={{ minHeight: 'calc(100vh - 56px)' }}
      data-testid={testIdRoot}>
      <div style={{ width: '448px', maxWidth: '90vw' }}>
        <div className="card border-0 shadow-lg rounded-4">
          <div className="card-body p-5 text-center">
            <h1 className="fw-bold fs-4 mb-3 text-dark">{heading}</h1>
            <p className="text-muted mb-4">Vui lòng tới bảng điều khiển để tiếp tục.</p>
            <a
              href={dashboardPath}
              className="btn btn-primary w-100 py-2 fw-semibold"
              data-testid={linkTestId}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                fontSize: '16px'
              }}>
              {linkLabel}
            </a>
            <div className="mt-3">
              <a
                href="/login"
                onClick={handleSwitchAccount}
                className="text-decoration-none text-muted"
                data-testid="switch-account-link"
                style={{ fontSize: '14px' }}>
                Đăng nhập với tài khoản khác
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Login Screen wrapper that handles navigation after login
const LoginScreenWrapper = ({ railsEnv, enabledProviders, locale, kidDomainMode, authStatus, authLoading }) => {
  const handleLoginSuccess = async ({ type, data }) => {
    console.log('[SPA] handleLoginSuccess called:', { type, data });

    // Check for redirect URL from server
    const redirectUrl = data?.redirect_url;
    if (redirectUrl) {
      console.log('[SPA] Using server-provided redirectUrl:', redirectUrl);
      window.location.href = redirectUrl;
      return;
    }

    // Fallback: calculate redirect based on type
    console.log('[SPA] No redirectUrl, using type-based fallback');
    if (type === 'kid') {
      navigateTo('/children');
    } else if (type === 'bypass') {
      // Bypass login - just reload to let server handle redirect
      window.location.reload();
    } else {
      navigateTo('/guardian');
    }
  };

  const handleLoginError = ({ type, error }) => {
    console.error('[SPA] Login error:', { type, error });
  };

  if (authLoading) {
    return <LoadingScreen />;
  }

  if (authStatus?.user) {
    if (authStatus.user_type === 'kid') {
      return <AlreadyLoggedInNotice userType="kid" dashboardPath="/children" />;
    }
    if (authStatus.user_type === 'guardian') {
      return <AlreadyLoggedInNotice userType="guardian" dashboardPath="/guardian" />;
    }
  }

  return (
    <LoginApp
      railsEnv={railsEnv}
      enabledProviders={enabledProviders}
      locale={locale}
      onLoginSuccess={handleLoginSuccess}
      onLoginError={handleLoginError}
      authMode="session"
      kidDomainMode={kidDomainMode}
    />
  );
};

// Main Router Content
const RouterContent = () => {
  const [currentPath, setCurrentPath] = useState(getCurrentPath);
  const [flashNotice, setFlashNotice] = useState(() => getInitialFlash().notice);
  const [flashAlert, setFlashAlert] = useState(() => getInitialFlash().alert);
  const [authStatus, setAuthStatus] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(null);
  const [onboardingLoaded, setOnboardingLoaded] = useState(false);

  const railsData = getRailsData();
  const locale = railsData.locale || 'vi';
  const railsEnv = railsData.railsEnv || 'production';
  const enabledProviders = railsData.enabledProviders || {};
  const kidDomainMode = railsData.kidDomainMode || false;

  // Clear the cache after first render
  useEffect(() => {
    initialFlashCache = null;
  }, []);

  // Listen for URL changes (back/forward navigation)
  useEffect(() => {
    const handlePopState = () => {
      const newPath = getCurrentPath();
      console.log('[SPA] handlePopState triggered, new path:', newPath);
      setCurrentPath(newPath);
      // Check for flash messages on navigation
      const { notice, alert } = getFlashFromQuery();
      if (notice) setFlashNotice(notice);
      if (alert) setFlashAlert(alert);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Check auth status on mount
  useEffect(() => {
    fetchAuthStatus().then((status) => {
      setAuthStatus(status);
      setAuthLoading(false);
    });
  }, []);

  // Fetch onboarding status when authenticated as guardian
  useEffect(() => {
    if (authStatus?.user_type === 'guardian' && !onboardingLoaded) {
      fetchOnboardingStatus().then((data) => {
        setOnboarding(data);
        setOnboardingLoaded(true);
      });
    }
  }, [authStatus, onboardingLoaded]);

  const route = getRouteFromPath(currentPath);
  console.log('[SPA] Rendering route:', route, 'authStatus:', authStatus, 'authLoading:', authLoading);

  // Track if we're redirecting to prevent multiple redirects
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Handle auth-related redirects in useEffect (not during render) to prevent infinite loops
  // This is critical: calling navigateTo() during render can cause React re-render loops
  // See Rollbar #49: RangeError: Maximum call stack size exceeded
  useEffect(() => {
    if (isRedirecting || authLoading) return;

    // Authenticated users on landing page -> redirect to dashboard
    if (route === 'landing' && authStatus?.user) {
      if (authStatus.user_type === 'kid') {
        setIsRedirecting(true);
        navigateTo('/children');
      } else if (authStatus.user_type === 'guardian') {
        setIsRedirecting(true);
        navigateTo('/guardian');
      }
      return;
    }

    // Unauthenticated/wrong user type on children page -> redirect to login
    if (route === 'children' && (!authStatus?.user || authStatus.user_type !== 'kid')) {
      setIsRedirecting(true);
      navigateTo('/login?as=kid');
      return;
    }

    // Unauthenticated/wrong user type on guardian page -> redirect to login
    if (route === 'guardian' && (!authStatus?.user || authStatus.user_type !== 'guardian')) {
      setIsRedirecting(true);
      navigateTo('/login');
      return;
    }
  }, [route, authStatus, authLoading, isRedirecting]);

  // Don't block login, landing, and public pages on auth loading
  const publicRoutes = ['login', 'landing', 'auth_callback', 'pricing', 'features', 'programs'];
  if (authLoading && !publicRoutes.includes(route)) {
    return <LoadingScreen />;
  }

  switch (route) {
    case 'landing':
      // Show loading if authenticated user (redirect will happen in useEffect)
      if (authStatus?.user) {
        return <LoadingScreen />;
      }
      return (
        <LandingPage
          locale={locale}
          loginPath="/login"
          flashNotice={flashNotice}
          flashAlert={flashAlert}
        />
      );

    case 'auth_callback':
      return <AuthCallback />;

    case 'login':
      return (
        <LoginScreenWrapper
          railsEnv={railsEnv}
          enabledProviders={enabledProviders}
          locale={locale}
          kidDomainMode={kidDomainMode}
          authStatus={authStatus}
          authLoading={authLoading}
        />
      );

    case 'pricing':
      return (
        <PricingPage
          locale={locale}
          loginPath="/login"
          flashNotice={flashNotice}
          flashAlert={flashAlert}
        />
      );

    case 'features':
      return (
        <FeaturesPage
          locale={locale}
          loginPath="/login"
          flashNotice={flashNotice}
          flashAlert={flashAlert}
        />
      );

    case 'programs':
      return (
        <ProgramsPage
          locale={locale}
          loginPath="/login"
          flashNotice={flashNotice}
          flashAlert={flashAlert}
        />
      );

    case 'children':
      // Show loading if not authenticated as kid (redirect handled in useEffect)
      if (!authStatus?.user || authStatus.user_type !== 'kid') {
        return <LoadingScreen />;
      }
      return (
        <UnifiedDashboard
          mode={DASHBOARD_MODES.KID}
          locale={locale}
          railsEnv={railsEnv}
          basePath="/children"
          buildTime={__BUILD_TIME__}
        />
      );

    case 'guardian':
      // Show loading if not authenticated as guardian (redirect handled in useEffect)
      if (!authStatus?.user || authStatus.user_type !== 'guardian') {
        return <LoadingScreen />;
      }
      // Wait for onboarding data to load before rendering guardian dashboard
      if (!onboardingLoaded) {
        return <LoadingScreen />;
      }
      return (
        <UnifiedDashboard
          mode={DASHBOARD_MODES.GUARDIAN}
          locale={locale}
          railsEnv={railsEnv}
          basePath="/guardian"
          flashNotice={flashNotice}
          flashAlert={flashAlert}
          onboarding={onboarding}
          buildTime={__BUILD_TIME__}
        />
      );

    default:
      return (
        <LandingPage
          locale={locale}
          loginPath="/login"
          flashNotice={flashNotice}
          flashAlert={flashAlert}
        />
      );
  }
};

// Main App Component
const App = () => {
  const [i18nReady, setI18nReady] = useState(false);
  const railsData = getRailsData();

  useEffect(() => {
    initI18n(railsData.locale || 'vi');
    setI18nReady(true);
  }, [railsData.locale]);

  if (!i18nReady) {
    return <LoadingScreen />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider authMode="session">
        <RouterContent />
      </AuthProvider>
    </QueryClientProvider>
  );
};

// Mount the app
const container = document.getElementById('root');
if (container) {
  const root = createRoot(container);
  root.render(<App />);
} else {
  console.error('[SPA] Root container not found');
}
